import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { Banner, Card } from '../../components/ui.tsx'
import { ApiError, post } from '../../lib/api.ts'
import { AuthCard, AuthHeading } from './AuthCard.tsx'

/**
 * Request a reset link.
 *
 * The response is identical whether or not the address is registered — otherwise this
 * form becomes a way to find out who has an account.
 */
export function Recover() {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState<{ message: string; devLink?: string } | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      setSent(await post<{ message: string; devLink?: string }>('/auth/recover', { email }))
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : String(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthCard width={420}>
      <AuthHeading title="Reset your password" sub="We'll email you a link to choose a new one." />

      {sent ? (
        <Card pad>
          <Banner tone="success" icon="✓" title="Reset link sent" flush>
            {sent.message}
          </Banner>

          {/* No mail transport is configured. Saying so, and handing over the link, beats
              a success message for an email that was never sent. */}
          {sent.devLink && (
            <div style={{ marginTop: 14 }}>
              <Banner tone="degraded" title="No mailer is configured on this deployment" flush>
                Nothing was actually emailed. For local use, the link is below — it expires in 60 minutes
                and works once.
                <div style={{ marginTop: 10 }}>
                  <Link className="btn outline block sm" to={sent.devLink}>
                    Open the reset link
                  </Link>
                </div>
              </Banner>
            </div>
          )}

          <button className="btn outline block sm" style={{ marginTop: 14 }} onClick={() => setSent(null)}>
            Use a different email
          </button>
          <p className="u-center u-sm u-faint" style={{ marginTop: 12 }}>
            <Link to="/login">Back to log in</Link>
          </p>
        </Card>
      ) : (
        <Card pad>
          <form onSubmit={submit} noValidate>
            <div className="field" style={{ marginBottom: 14 }}>
              <label htmlFor="rc-email">Email</label>
              <input
                className="input"
                type="email"
                id="rc-email"
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {err && (
                <span className="help warn" role="alert">
                  {err}
                </span>
              )}
            </div>
            <button type="submit" className="btn primary block lg" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <p className="u-center u-sm u-faint" style={{ marginTop: 12 }}>
              Remembered it? <Link to="/login">Back to log in</Link>
            </p>
          </form>
        </Card>
      )}
    </AuthCard>
  )
}
